import React, {useState, useCallback, useEffect} from 'react';
import styled from 'styled-components';
import Hidden from '@material-ui/core/Hidden/Hidden';
import Grid from '@material-ui/core/Grid';
import {
    GridContainer,
    CenteredGrid,
    OutlinedCard,
    VerticalSpacer,
    Typography,
    EyebrowProps,
    ImageProps,
    Button,
    ImageCard,
    CmsButtonProps
} from '../../elements';
import {AccentColorOptions, useBreakpointOnly} from 'epic-ue-theme';
import {AnimateOnViewWrap, WithCaptionProps} from '../../wraps';
import Carousel from 'nuka-carousel';
import {ArrowControls} from '../features/FeaturesCarousel.controls';

const Styled = styled.div<{type: string; afterRender: boolean}>`
    .description .paragraph {
        color: ${(p) => p.theme.palette.text.secondary};
    }

    .section-card-carousel {
        position: relative;
        .slider {
            padding-bottom: 150px;
            outline: none;
            .slider-list {
                // important used here to override inline styles being set by nuka
                cursor: inherit !important;

                // important used here to override inline styles being set by nuka 
                // height is too big due to AnimateOnViewWrap within each Slide Element 
                .slider-slide {
                    height: auto !important;
                }
            }
            .ue-outlined-card {
                flex-flow: column nowrap;
            }
        }

        li {
            &:focus-visible {
                outline: none;
            }
            span.slide.not-clickable {
                &:focus-visible {
                    outline: none;
                }
                div,
                img {
                    &:focus-visible {
                        outline: none;
                    }
                }
            }
            & > span,
            & > a {
                display: block;
                height: ${(p) => (p.afterRender ? '100%' : '')};
            }
        }

        .slider-control-bottomright {
            // important used here to override inline styles being set by nuka

            ${(p) => p.theme.breakpoints.down('sm')} {
                right: -3px !important;
            }

            ${(p) => p.theme.breakpoints.up('md')} {
                right: -6px !important;
            }

            ${(p) => p.theme.breakpoints.up('lg')} {
                right: -10px !important;
            }

            width: 100px;
            button {
                display: inline-block;
                font-size: 2em;
                &:first-child {
                    margin-right: 10px;
                }
            }
        }
    }
`;

const CardCarouselTabs = styled.div``;

const CardCarouselTab = styled.div<{active?: boolean}>`
    margin-bottom: ${(p) => p.theme.spacing(2)}px;
    line-height: 20px;
    &:not(:hover) {
        width: fit-content;
        color: ${(p) =>
            p.active
                ? 'inherit'
                : (p) =>
                      p.theme.ifLight(
                          p,
                          p.theme.palette.neutral.steel,
                          p.theme.palette.neutral.silver
                      )};
        .underline:after {
            visibility: ${(p) => (p.active ? 'inherit' : 'hidden')};
        }
    }
`;

export const ProgressBar = styled.div<{color: AccentColorOptions; offset: number; amount: number}>`
    position: absolute;
    bottom: 100px;
    width: calc(100% - 32px);
    height: 2px;
    left: 16px;
    background-color: ${(p) =>
        p.theme.ifLight(p, p.theme.palette.neutral.silver, p.theme.palette.background.paper)};
    overflow: hidden;
    span {
        transition: 0.5s ease;
        height: 2px;
        width: ${(p) => p.amount}%;
        margin-left: ${(p) => p.offset}%;
        background-color: ${({color, ...p}) => p.theme.palette.accent[color]};
        display: block;
    }
`;

export const SlideCaption = styled.div`
    letter-spacing: 3px;
    line-height: 18px;
    text-transform: uppercase;
    margin-top: 16px;
    font-weight: 500;
    font-size: 13px;
`;

export interface CardCarouselTabs {
    label?: string;
    jumpToSlide?: number;
}
export interface SlideProps {
    eyebrow: EyebrowProps;
    image?: ImageProps;
    href?: string;
    title?: string;
    caption?: WithCaptionProps;
    description?: string;
}

export interface CardCarouselProps {
    accentColor?: AccentColorOptions;
    button?: CmsButtonProps;
    description?: string;
    heading?: string;
    tabs?: CardCarouselTabs[];
    slides?: SlideProps[];
    type?: string;
    hideLeftPanel?: boolean;
    noGridContainer?: boolean;
}

export const CardCarousel = (props: CardCarouselProps): JSX.Element => {
    const {
        slides = [],
        accentColor = 'sky',
        heading,
        button,
        type = 'outline',
        description,
        tabs = [],
        hideLeftPanel = false,
        noGridContainer
    } = props;

    const isXs = useBreakpointOnly('xs');
    const isSm = useBreakpointOnly('sm');

    const [slideIndex, setSlideIndex] = useState(0);
    const beforeSlideCallback = useCallback(
        (_, nextIndex) => {
            setSlideIndex(nextIndex);
        },
        [setSlideIndex]
    );

    const [afterRender, setAfterRender] = useState(false);
    useEffect(() => {
        // Wait until Nuka carousel renders the initial slide height, then apply new height style based on state
        setTimeout(() => {
            setAfterRender(true);
        }, 100);
    }, [setAfterRender]);

    let slidesToShow = hideLeftPanel ? 4 : 3.2;
    let slidesToScroll = 3;
    if (isSm) {
        slidesToShow = 2.2;
        slidesToScroll = 2;
    } else if (isXs) {
        slidesToShow = 1.2;
        slidesToScroll = 1;
    }

    const slideAmount = slides.length;
    const w = 40;
    const i = slideIndex;
    const b = 100 - w;
    let o = 0;

    let hideSliderControls;
    if (slideAmount === slidesToShow) {
        hideSliderControls = 1;
    }

    if (slideIndex !== 0) {
        o = Math.floor((b / slideAmount) * (i + slidesToScroll));
    }

    const atCarouselEnd = slideIndex >= slides.length - slidesToShow;
    const slideElems: JSX.Element[] = [];
    const leftArrowDisable = o === 0;
    const rightArrowDisable = o === b;
    let slideContainerElem: JSX.Element = <div />;

    let progressBarAnimationDelay = 100;

    slides.map((slide, i) => {
        const animateDelay = (i + 1) * 50;

        //progress bar needs to animation in after all the cards
        progressBarAnimationDelay = animateDelay;

        const slideCaption = slide.caption?.text && (
            <SlideCaption>{slide.caption.text}</SlideCaption>
        );

        const clickable = Boolean(slide.href);
        const clickableClass = clickable ? 'clickable' : 'not-clickable';

        if (type === 'outline') {
            const outlineCard = (
                <OutlinedCard
                    accentColor={accentColor}
                    href={slide.href}
                    title={slide.title}
                    fillParentSize={true}
                    groupedHeight={true}
                    eyebrow={{text: slide.eyebrow.text || ''}}
                    description={slide.description}
                />
            );
            if (slideAmount < 4) {
                slideElems.push(
                    <AnimateOnViewWrap translateY={animateDelay}>
                        <Grid item xs={12} lg={4} key={`outline-card-c-title-${i}`}>
                            {outlineCard}
                            {slideCaption}
                        </Grid>
                    </AnimateOnViewWrap>
                );
            } else {
                slideElems.push(
                    <AnimateOnViewWrap translateY={animateDelay}>
                        <span
                            className={`slide slide-outline-card ${clickableClass}`}
                            key={`outline-card-c-title-${i}`}>
                            {outlineCard}
                            {slideCaption}
                        </span>
                    </AnimateOnViewWrap>
                );
            }
        } else if (type === 'image') {
            if (slide.image) {
                const imageCard = (
                    <ImageCard
                        accentColor={accentColor}
                        image={{
                            src: slide.image.src,
                            alt: slide.image.alt
                        }}
                        link={{
                            label: '',
                            href: slide.href
                        }}
                        title={slide.title}
                        description={slide.description}
                        eyebrow={{text: slide.eyebrow.text || ''}}
                    />
                );
                if (slideAmount < 4) {
                    slideElems.push(
                        <AnimateOnViewWrap translateY={animateDelay}>
                            <Grid item xs={12} lg={4} key={`image-card-c-title-${i}`}>
                                {imageCard}
                                {slideCaption}
                            </Grid>
                        </AnimateOnViewWrap>
                    );
                } else {
                    slideElems.push(
                        <AnimateOnViewWrap translateY={animateDelay}>
                            <span
                                className={`slide slide-image-card ${clickableClass}`}
                                key={`image-card-c-title-${i}`}>
                                {imageCard}
                                {slideCaption}
                            </span>
                        </AnimateOnViewWrap>
                    );
                }
            } else {
                slideElems.push(
                    <AnimateOnViewWrap translateY={animateDelay}>
                        <OutlinedCard {...slide} key={`outline-card-c-title-${i}`} />
                    </AnimateOnViewWrap>
                );
            }
        }
    });

    if (slideAmount < 4) {
        slideContainerElem = (
            <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="stretch"
                spacing={3}>
                {slideElems}
            </Grid>
        );
    } else {
        slideContainerElem = (
            <Carousel
                dragging={true}
                edgeEasing="easeCircleOut"
                enableKeyboardControls
                framePadding={`0`}
                slideIndex={slideIndex}
                slidesToShow={slidesToShow}
                slidesToScroll={slidesToScroll}
                cellSpacing={24}
                beforeSlide={beforeSlideCallback}
                renderBottomCenterControls={null}
                renderCenterLeftControls={null}
                renderCenterRightControls={null}
                renderBottomRightControls={({previousSlide, nextSlide}) => (
                    <>
                        {!hideSliderControls && (
                            <ArrowControls
                                small={true}
                                onClick={previousSlide}
                                disabled={leftArrowDisable}
                                accentColor={accentColor}
                                type="left"
                            />
                        )}
                        {!hideSliderControls && (
                            <ArrowControls
                                small={true}
                                onClick={nextSlide}
                                disabled={rightArrowDisable}
                                accentColor={accentColor}
                                type="right"
                            />
                        )}
                    </>
                )}>
                {slideElems}
            </Carousel>
        );
    }

    const cardCarouselBody = (
        <CenteredGrid justifyContent={'flex-start'} alignItems={'flex-start'}>
            {!hideLeftPanel && (
                <Grid item sm={12} md={8} lg={4}>
                    <CenteredGrid>
                        <Grid item xs={12} lg={10}>
                            <AnimateOnViewWrap translateY={50}>
                                {heading && (
                                    <>
                                        <Typography variant="h2" big={false} as="h3">
                                            {heading}
                                        </Typography>
                                        <VerticalSpacer variant="eyebrow" />
                                    </>
                                )}
                                {description && (
                                    <div className="description">
                                        <Typography variant="p">{description}</Typography>
                                        <VerticalSpacer variant="heading" />
                                    </div>
                                )}
                            </AnimateOnViewWrap>
                            {tabs && tabs.length > 0 && (
                                <AnimateOnViewWrap translateY={50}>
                                    <CardCarouselTabs>
                                        {tabs.map(({jumpToSlide = 1, label = ''}, index) => {
                                            const nextTab = tabs[index + 1];
                                            const nextTabStart =
                                                nextTab?.jumpToSlide && nextTab?.jumpToSlide - 1;
                                            const afterCurrentTabStart =
                                                slideIndex >= jumpToSlide - 1;
                                            const beforeNextTabStart = nextTabStart
                                                ? slideIndex < nextTabStart
                                                : false;
                                            const notLastTabActive =
                                                !atCarouselEnd &&
                                                (nextTab
                                                    ? afterCurrentTabStart && beforeNextTabStart
                                                    : afterCurrentTabStart);
                                            const lastTabActive = atCarouselEnd && !nextTab;
                                            return (
                                                <CardCarouselTab
                                                    key={index}
                                                    active={notLastTabActive || lastTabActive}>
                                                    <Button
                                                        type="tertiary"
                                                        onClick={() =>
                                                            setSlideIndex(jumpToSlide - 1)
                                                        }
                                                        color={accentColor}>
                                                        {label}
                                                    </Button>
                                                </CardCarouselTab>
                                            );
                                        })}
                                    </CardCarouselTabs>
                                    <VerticalSpacer
                                        variant="custom"
                                        spacing={5}
                                        downSmSpacing={4}
                                    />
                                </AnimateOnViewWrap>
                            )}
                            {button && button.label && (
                                <AnimateOnViewWrap translateY={50}>
                                    <Button
                                        type={button.type || 'secondary'}
                                        href={button.href}
                                        color={accentColor}>
                                        {button.label}
                                    </Button>
                                    <VerticalSpacer variant="custom" spacing={5} />
                                </AnimateOnViewWrap>
                            )}
                        </Grid>
                        <VerticalSpacer variant="custom" spacing={3} downSmSpacing={5} />
                        <Hidden mdDown>
                            <Grid item md={2} />
                        </Hidden>
                    </CenteredGrid>
                </Grid>
            )}

            <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={hideLeftPanel ? 12 : 8}
                className="section-card-carousel">
                <AnimateOnViewWrap translateY={progressBarAnimationDelay + 50}>
                    {slideContainerElem}
                    {slideAmount > 3 && !hideSliderControls && (
                        <ProgressBar color={accentColor} amount={w} offset={o}>
                            <span />
                        </ProgressBar>
                    )}
                </AnimateOnViewWrap>
            </Grid>
        </CenteredGrid>
    );

    return (
        <Styled type={type} afterRender={afterRender}>
            {noGridContainer ? cardCarouselBody : <GridContainer>{cardCarouselBody}</GridContainer>}
        </Styled>
    );
};
